// src/pages/Amostras/EteRelatorioModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Typography,
    Alert,
    CircularProgress,
} from '@mui/material';

import {
    baixarRelatorioEtePdf,
    baixarRelatorioEteHtml,
} from '../../infra/api/templateGenerateApi';

const norm = (s) =>
    String(s || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase();

const guessEntrada = (a) => {
    const id = norm(a?.identificacao);
    return (
        id.includes('ENTRADA') ||
        id.includes('AFLUENTE') ||
        id.includes('INLET')
    );
};

const guessSaida = (a) => {
    const id = norm(a?.identificacao);
    return (
        id.includes('SAIDA') || id.includes('EFLUENTE') || id.includes('OUTLET')
    );
};

function saveBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}

const EteRelatorioModal = ({ open, onClose, amostras = [] }) => {
    const [entradaId, setEntradaId] = useState('');
    const [saidaId, setSaidaId] = useState('');

    const [downloading, setDownloading] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (!open) {
            setEntradaId('');
            setSaidaId('');
            setDownloading(null);
            setErrorMsg('');
        }
    }, [open]);

    const { entradas, saidas } = useMemo(() => {
        const list = Array.isArray(amostras) ? amostras : [];
        const e = list.filter(guessEntrada);
        const s = list.filter(guessSaida);
        return {
            entradas: e.length ? e : list,
            saidas: s.length ? s : list,
        };
    }, [amostras]);

    const invalido = useMemo(
        () => entradaId && saidaId && entradaId === saidaId,
        [entradaId, saidaId]
    );
    const podeGerar = Boolean(
        entradaId && saidaId && !invalido && !downloading
    );

    const gerar = async (tipo) => {
        try {
            setErrorMsg('');
            setDownloading(tipo);

            if (tipo === 'pdf') {
                const blob = await baixarRelatorioEtePdf(entradaId, saidaId);
                saveBlob(blob, 'relatorio-ete.pdf');
            } else {
                const blob = await baixarRelatorioEteHtml(entradaId, saidaId);
                saveBlob(blob, 'relatorio-ete.html');
            }
        } catch (e) {
            const msg =
                e?.response?.data?.message ||
                e?.message ||
                'Falha ao baixar arquivo.';
            setErrorMsg(msg);
        } finally {
            setDownloading(null);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={downloading ? undefined : onClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>Relatório ETE</DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Selecione a análise de <b>Entrada (Afluente)</b> e a de{' '}
                        <b>Saída (Efluente)</b>.
                    </Typography>

                    <FormControl
                        fullWidth
                        size="small"
                        disabled={Boolean(downloading)}
                    >
                        <InputLabel>Análise de Entrada (Afluente)</InputLabel>
                        <Select
                            label="Análise de Entrada (Afluente)"
                            value={entradaId}
                            onChange={(e) => setEntradaId(e.target.value)}
                        >
                            {entradas.map((a) => (
                                <MenuItem
                                    key={a.id || a._id}
                                    value={a.id || a._id}
                                >
                                    {a.identificacao}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl
                        fullWidth
                        size="small"
                        disabled={Boolean(downloading)}
                    >
                        <InputLabel>Análise de Saída (Efluente)</InputLabel>
                        <Select
                            label="Análise de Saída (Efluente)"
                            value={saidaId}
                            onChange={(e) => setSaidaId(e.target.value)}
                        >
                            {saidas.map((a) => (
                                <MenuItem
                                    key={a.id || a._id}
                                    value={a.id || a._id}
                                >
                                    {a.identificacao}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {invalido && (
                        <Alert severity="warning">
                            Entrada e saída não podem ser a mesma análise.
                        </Alert>
                    )}

                    {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={Boolean(downloading)}>
                    Cancelar
                </Button>

                <Button
                    variant="outlined"
                    disabled={!podeGerar}
                    onClick={() => gerar('html')}
                    startIcon={
                        downloading === 'html' ? (
                            <CircularProgress size={16} />
                        ) : null
                    }
                >
                    {downloading === 'html' ? 'Baixando...' : 'Baixar HTML'}
                </Button>

                <Button
                    variant="contained"
                    disabled={!podeGerar}
                    onClick={() => gerar('pdf')}
                    startIcon={
                        downloading === 'pdf' ? (
                            <CircularProgress size={16} />
                        ) : null
                    }
                >
                    {downloading === 'pdf' ? 'Baixando...' : 'Baixar PDF'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EteRelatorioModal;
